/**
 * AnimatedIntro — CSS + Framer-Motion 8-second WakaCasa branded intro.
 *
 * Used as the fallback for the Sora 2 clip in the agent tutorial video and
 * also directly embedded in the first-login welcome modal.
 *
 * Aesthetic mirrors the Sora prompt: navy void, drifting gold particles,
 * palm silhouette dissolving into the WakaCasa monogram.
 *
 * Props:
 *   onDone?  callback fired at t=8000ms (used by the modal to auto-advance)
 *   loop?    when true, the animation restarts every 8 s (used for demos)
 */
import { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';

const NAVY = '#0B192C';
const GOLD = '#D4AF37';

function Particle({ delay, x, y, size }) {
  return (
    <motion.div
      initial={{ opacity: 0, x, y, scale: 0.6 }}
      animate={{
        opacity: [0, 0.9, 0.9, 0],
        x: x + 120,
        y: y - 40,
        scale: [0.6, 1, 1, 0.4],
      }}
      transition={{ duration: 6, delay, ease: 'easeOut' }}
      style={{
        position: 'absolute',
        width: size,
        height: size,
        background: GOLD,
        borderRadius: '50%',
        filter: `blur(${size / 3}px)`,
        boxShadow: `0 0 ${size * 2}px ${GOLD}`,
      }}
    />
  );
}

export default function AnimatedIntro({ onDone, loop = false }) {
  const doneRef = useRef(false);
  const particles = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 22; i += 1) {
      arr.push({
        delay: i * 0.18,
        x: 40 + Math.random() * 800,
        y: 60 + Math.random() * 380,
        size: 4 + Math.random() * 10,
      });
    }
    return arr;
  }, []);

  useEffect(() => {
    if (!onDone || doneRef.current) return undefined;
    const t = setTimeout(() => {
      doneRef.current = true;
      onDone();
    }, 8000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div
      data-testid="animated-intro"
      className="relative w-full aspect-[16/9] overflow-hidden select-none"
      style={{
        background: `radial-gradient(circle at 50% 55%, #142944 0%, ${NAVY} 55%, #060d18 100%)`,
      }}
    >
      {/* Drifting gold particles */}
      <div className="absolute inset-0" aria-hidden>
        {particles.map((p, i) => (
          <Particle key={i} {...p} />
        ))}
      </div>

      {/* Soft palm-silhouette sweep (SVG) */}
      <motion.svg
        viewBox="0 0 800 450"
        className="absolute inset-0 w-full h-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.35, 0.35, 0] }}
        transition={{ duration: 6, delay: 0.6, times: [0, 0.25, 0.7, 1] }}
        aria-hidden
      >
        <defs>
          <linearGradient id="palmGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={GOLD} stopOpacity="0.65" />
            <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Stylised palm-leaf silhouette */}
        <path
          d="M400 460 C400 420 380 380 360 340 C340 300 310 260 340 240 C370 220 395 250 400 290 C405 250 430 220 460 240 C490 260 460 300 440 340 C420 380 400 420 400 460 Z"
          fill="url(#palmGrad)"
        />
        <path
          d="M400 320 C360 290 310 285 280 300 C320 305 350 320 400 340"
          stroke={GOLD}
          strokeOpacity="0.5"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M400 320 C440 290 490 285 520 300 C480 305 450 320 400 340"
          stroke={GOLD}
          strokeOpacity="0.5"
          strokeWidth="2"
          fill="none"
        />
      </motion.svg>

      {/* Monogram reveal — final beat */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: [0, 0, 0.9, 1], scale: [0.85, 0.85, 1.02, 1] }}
        transition={{ duration: 8, times: [0, 0.55, 0.85, 1], ease: 'easeOut' }}
      >
        <div
          className="flex flex-col items-center"
          style={{ filter: `drop-shadow(0 0 36px ${GOLD}88)` }}
        >
          <svg width="140" height="140" viewBox="0 0 100 100" aria-hidden>
            {/* Architectural W-house monogram */}
            <path
              d="M20 78 L28 40 L38 62 L50 30 L62 62 L72 40 L80 78 Z"
              fill="none"
              stroke={GOLD}
              strokeWidth="2.4"
              strokeLinejoin="round"
            />
            <path
              d="M20 78 L80 78"
              stroke={GOLD}
              strokeOpacity="0.55"
              strokeWidth="1.2"
            />
          </svg>
          <div
            className="mt-4 font-heading tracking-[0.28em] text-lg"
            style={{ color: GOLD, fontFamily: '"Playfair Display", serif' }}
          >
            WAKACASA
          </div>
        </div>
      </motion.div>

      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'><filter id=\'n\'><feTurbulence baseFrequency=\'0.85\' numOctaves=\'2\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>")',
        }}
        aria-hidden
      />

      {loop && (
        <style>{`
          [data-testid="animated-intro"] { animation: waka-loop 8s infinite; }
          @keyframes waka-loop { 0%,100% { opacity: 1; } 99% { opacity: 1; } }
        `}</style>
      )}
    </div>
  );
}
